import React, { useState, useEffect } from 'react';
import { Wind, Plus, Edit, Trash2, CheckCircle, X, BarChart3, Brain } from 'lucide-react';
import { Button, Card, Input, Select, Alert, Spinner } from '../design-system';
import groundingService from '../../services/groundingService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import analyticsService from '../../services/analyticsService';

/**
 * Grounding Assignment Component for Therapists
 * Allows therapists to assign and manage grounding techniques for patients
 */
const GroundingAssignment = ({ patient }) => {
  const { user, userInfo } = useAuth();
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    loadAssignment();
  }, [patient.patientId]);

  const loadAssignment = async () => {
    try {
      setLoading(true);
      const response = await groundingService.getAssignment(user, patient.patientId);
      setCurrentAssignment(response.assignment);
      
      // Load analytics if assignment exists
      if (response.assignment) {
        loadAnalytics();
      }
    } catch (error) {
      console.error('Error loading grounding assignment:', error);
      toast.error('Failed to load grounding assignment');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await groundingService.getAnalytics(user, patient.patientId, 'month');
      setAnalytics(response.analytics);
    } catch (error) {
      console.error('Error loading grounding analytics:', error);
      // Don't show error toast for analytics, it's secondary
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleAssignTechnique = async (techniqueConfig) => {
    try {
      setSaving(true);
      const response = await groundingService.assignTechnique(
        user,
        patient.patientId,
        techniqueConfig
      );
      setCurrentAssignment(response.assignment);
      setShowAssignmentModal(false);
      toast.success('Grounding technique assigned successfully!');
      
      // Track grounding technique assignment event
      try {
        analyticsService.track('grounding_technique_assigned', {
          groundingId: response.assignment?.groundingId,
          patientId: patient.patientId,
          therapistId: userInfo?.userId,
          techniqueType: techniqueConfig.techniqueType,
          isBreathing: techniqueConfig.techniqueType === 'breathing',
          isThoughtsFacts: techniqueConfig.techniqueType === 'thoughts-facts',
          ...(techniqueConfig.techniqueType === 'breathing' && {
            inhaleDuration: techniqueConfig.inhaleDuration,
            holdDuration: techniqueConfig.holdDuration,
            exhaleDuration: techniqueConfig.exhaleDuration,
            cycles: techniqueConfig.cycles
          })
        });
      } catch (analyticsError) {
        // Don't break assignment if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
    } catch (error) {
      console.error('Error assigning technique:', error);
      toast.error(error.message || 'Failed to assign grounding technique');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card padding="large">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <Spinner size="medium" />
            <span className="text-gray-600">Loading grounding assignment...</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card padding="large">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900">Grounding Technique</h3>
          <Button 
            onClick={() => setShowAssignmentModal(true)}
            variant={currentAssignment ? "outline" : "primary"}
            size="small"
            className="flex items-center gap-1.5"
          >
            {currentAssignment ? (
              <>
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Edit</span>
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add</span>
              </>
            )}
          </Button>
        </div>
        
        {currentAssignment ? (
          <div className="space-y-3">
            <div className="bg-white p-4 md:p-5 rounded-lg border border-gray-200">
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)'}}>
                  {currentAssignment.techniqueType === 'breathing' ? (
                    <Wind className="h-6 w-6 md:h-7 md:w-7" style={{color: 'var(--color-primary)'}} />
                  ) : (
                    <Brain className="h-6 w-6 md:h-7 md:w-7" style={{color: 'var(--color-primary)'}} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
                    {currentAssignment.techniqueType === 'breathing' ? 'Breathing Exercise' : 'Thoughts and Facts'}
                  </h4>
                  {currentAssignment.techniqueType === 'breathing' && (
                    <p className="text-sm md:text-base text-gray-600">
                      {currentAssignment.techniqueConfig.cycles} cycles • {currentAssignment.techniqueConfig.inhaleDuration}s in, {currentAssignment.techniqueConfig.holdDuration}s hold, {currentAssignment.techniqueConfig.exhaleDuration}s out
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Analytics Section */}
            {analyticsLoading ? (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-3">
                  <BarChart3 className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Current Assignment Analytics</span>
                </div>
                <div className="flex items-center justify-center py-4">
                  <div className="flex items-center gap-2">
                    <Spinner size="small" />
                    <span className="text-sm text-gray-600">Loading analytics...</span>
                  </div>
                </div>
              </div>
            ) : analytics && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-3">
                  <BarChart3 className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Current Assignment Analytics</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">{analytics.totalSessions}</div>
                    <div className="text-sm text-gray-600">Total Sessions</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">
                      {analytics.totalDuration ? 
                        `${Math.floor(analytics.totalDuration / 60)}m ${analytics.totalDuration % 60}s` : 
                        '0m 0s'
                      }
                    </div>
                    <div className="text-sm text-gray-600">Total Duration</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Wind className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Grounding Technique Assigned</h4>
            <p className="text-gray-600 mb-4">
              Assign a grounding technique to help {patient.name} manage stress and anxiety.
            </p>
            <Button onClick={() => setShowAssignmentModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Assign Technique
            </Button>
          </div>
        )}
      </Card>

      {showAssignmentModal && (
        <GroundingAssignmentModal
          patient={patient}
          currentAssignment={currentAssignment}
          onAssign={handleAssignTechnique}
          onClose={() => setShowAssignmentModal(false)}
          saving={saving}
        />
      )}
    </>
  );
};

/**
 * Grounding Assignment Modal
 * Form for assigning or updating grounding techniques
 */
const GroundingAssignmentModal = ({ patient, currentAssignment, onAssign, onClose, saving }) => {
  const [formData, setFormData] = useState({
    techniqueType: 'breathing',
    inhaleDuration: 10,
    holdDuration: 5,
    exhaleDuration: 10,
    cycles: 3,
    instructions: 'Focus on your breath and let go of tension'
  });

  // Debug form data changes
  useEffect(() => {
    console.log('Form data updated - techniqueType:', formData.techniqueType);
  }, [formData.techniqueType]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (currentAssignment) {
      setFormData({
        techniqueType: currentAssignment.techniqueType,
        inhaleDuration: currentAssignment.techniqueConfig.inhaleDuration || 10,
        holdDuration: currentAssignment.techniqueConfig.holdDuration || 5,
        exhaleDuration: currentAssignment.techniqueConfig.exhaleDuration || 10,
        cycles: currentAssignment.techniqueConfig.cycles || 3,
        instructions: currentAssignment.techniqueConfig.instructions || ''
      });
    }
  }, [currentAssignment]);

  const handleInputChange = (field, value) => {
    // Ensure we only store primitive values, not DOM elements
    const cleanValue = typeof value === 'object' && value !== null ? value.value || value : value;
    console.log(`handleInputChange: ${field} =`, cleanValue);
    setFormData(prev => ({ ...prev, [field]: cleanValue }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.techniqueType === 'breathing') {
      if (formData.inhaleDuration < 1 || formData.inhaleDuration > 60) {
        newErrors.inhaleDuration = 'Must be between 1 and 60 seconds';
      }

      if (formData.holdDuration < 0 || formData.holdDuration > 60) {
        newErrors.holdDuration = 'Must be between 0 and 60 seconds';
      }

      if (formData.exhaleDuration < 1 || formData.exhaleDuration > 60) {
        newErrors.exhaleDuration = 'Must be between 1 and 60 seconds';
      }

      if (formData.cycles < 1 || formData.cycles > 20) {
        newErrors.cycles = 'Must be between 1 and 20 cycles';
      }
    }

    if (!formData.instructions.trim()) {
      newErrors.instructions = 'Instructions are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const techniqueConfig = {
      techniqueType: formData.techniqueType,
      instructions: formData.instructions.trim()
    };

    // Only add breathing-specific fields if it's a breathing exercise
    if (formData.techniqueType === 'breathing') {
      techniqueConfig.inhaleDuration = parseInt(formData.inhaleDuration);
      techniqueConfig.holdDuration = parseInt(formData.holdDuration);
      techniqueConfig.exhaleDuration = parseInt(formData.exhaleDuration);
      techniqueConfig.cycles = parseInt(formData.cycles);
    }

    onAssign(techniqueConfig);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">
            {currentAssignment ? 'Update Grounding Technique' : 'Assign Grounding Technique'}
          </h3>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Technique Type
            </label>
            <Select
              value={formData.techniqueType}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ ...prev, techniqueType: value }));
              }}
              options={[
                { value: 'breathing', label: 'Breathing Exercise' },
                { value: 'thoughts-facts', label: 'Thoughts and Facts' }
              ]}
            />
            <div className="text-xs text-gray-500 mt-1">
              Current value: {String(formData.techniqueType)}
            </div>
          </div>

          {formData.techniqueType === 'breathing' && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inhale (sec)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={formData.inhaleDuration}
                    onChange={(e) => handleInputChange('inhaleDuration', e.target.value)}
                    error={errors.inhaleDuration}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hold (sec)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="60"
                    value={formData.holdDuration}
                    onChange={(e) => handleInputChange('holdDuration', e.target.value)}
                    error={errors.holdDuration}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exhale (sec)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={formData.exhaleDuration}
                    onChange={(e) => handleInputChange('exhaleDuration', e.target.value)}
                    error={errors.exhaleDuration}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Cycles
                </label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.cycles}
                  onChange={(e) => handleInputChange('cycles', e.target.value)}
                  error={errors.cycles}
                />
              </div>
            </>
          )}

          {formData.techniqueType === 'thoughts-facts' && (
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>Thoughts and Facts</strong> helps patients examine catastrophic thoughts objectively through structured questions:
              </p>
              <ul className="text-sm text-purple-600 mt-2 space-y-1">
                <li>• What's on your mind?</li>
                <li>• What's supporting this thought?</li>
                <li>• What's against this thought?</li>
                <li>• What's a balanced view?</li>
              </ul>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instructions
            </label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              value={formData.instructions}
              onChange={(e) => handleInputChange('instructions', e.target.value)}
              placeholder="Enter instructions for the patient..."
            />
            {errors.instructions && (
              <p className="text-red-600 text-sm mt-1">{errors.instructions}</p>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="small" className="mr-2" />
                  {currentAssignment ? 'Updating...' : 'Assigning...'}
                </>
              ) : (
                currentAssignment ? 'Update Assignment' : 'Assign Technique'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroundingAssignment;
